// src/ui/components/atelierEntranceGate.ts — Full-Screen Victorian Entrance Gate for Family Passcode & Profile Selection

import { tokens } from '../designTokens';
import { AuthManager, DEFAULT_FAMILY_PASSCODE } from '../../auth/authManager';
import { soundEngine } from '@audio/soundEngine';

export interface AtelierEntranceGateOptions {
  onUnlock: () => void;
}

export class AtelierEntranceGate {
  private overlay: HTMLElement;
  private onUnlock: () => void;
  private selectedRole: 'student' | 'parent' = 'student';

  constructor(options: AtelierEntranceGateOptions) {
    this.onUnlock = options.onUnlock;
    this.overlay = document.createElement('div');
    this.overlay.id = 'atelier-entrance-gate';
    this.render();
  }

  getElement(): HTMLElement {
    return this.overlay;
  }

  show(): void {
    if (!document.body.contains(this.overlay)) {
      document.body.appendChild(this.overlay);
    }
    const input = this.overlay.querySelector<HTMLInputElement>('#gate-passcode-input');
    input?.focus();
  }

  close(): void {
    this.overlay.style.opacity = '0';
    this.overlay.style.transition = 'opacity 0.35s ease';
    setTimeout(() => {
      if (document.body.contains(this.overlay)) {
        document.body.removeChild(this.overlay);
      }
    }, 350);
  }

  private render(): void {
    this.overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(7, 20, 36, 0.88);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 100000;
      padding: 16px;
      opacity: 1;
    `;

    this.overlay.innerHTML = `
      <div
        class="morris-card"
        style="
          max-width: 480px;
          width: 100%;
          background: #FAF8F5;
          border: 3px double ${tokens.color.gildedBrass};
          border-radius: 24px;
          padding: 36px 32px;
          box-shadow: 0 20px 50px rgba(7, 20, 36, 0.5);
          text-align: center;
          position: relative;
          animation: popIn 0.3s ease-out;
        "
      >
        <div style="display: flex; justify-content: center; margin-bottom: 12px;">
          <div style="
            width: 72px;
            height: 72px;
            border-radius: 50%;
            background: #FFFDF9;
            border: 2px solid ${tokens.color.gildedBrass};
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2.2rem;
            box-shadow: 0 4px 12px rgba(7, 20, 36, 0.15);
          ">🦞</div>
        </div>

        <div style="font-size: 0.82rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em;">
          Private Marine Sanctuary
        </div>

        <h2 style="font-size: 1.6rem; color: ${tokens.color.morrisIndigo}; margin: 8px 0 10px; font-family: 'Cinzel', serif;">
          Clawey 11+ Atelier
        </h2>

        <p style="font-size: 0.95rem; color: #3A4D66; margin: 0 0 20px; line-height: 1.5;">
          Welcome to our deep-sea naturalist atelier. Please enter the family passcode to enter.
        </p>

        <!-- Profile Selector Chips -->
        <div style="display: flex; gap: 10px; margin-bottom: 20px; justify-content: center;">
          <button
            type="button"
            id="role-student-btn"
            class="touch-target"
            style="
              flex: 1;
              padding: 10px 14px;
              border-radius: 12px;
              border: 2px solid ${this.selectedRole === 'student' ? tokens.color.morrisIndigo : 'rgba(26,54,93,0.2)'};
              background: ${this.selectedRole === 'student' ? 'rgba(26,54,93,0.08)' : '#FFFDF9'};
              color: ${tokens.color.morrisIndigo};
              font-weight: 800;
              font-size: 0.9rem;
              cursor: pointer;
            "
          >
            ⭐ Tudor (Learner)
          </button>
          <button
            type="button"
            id="role-parent-btn"
            class="touch-target"
            style="
              flex: 1;
              padding: 10px 14px;
              border-radius: 12px;
              border: 2px solid ${this.selectedRole === 'parent' ? tokens.color.morrisIndigo : 'rgba(26,54,93,0.2)'};
              background: ${this.selectedRole === 'parent' ? 'rgba(26,54,93,0.08)' : '#FFFDF9'};
              color: ${tokens.color.morrisIndigo};
              font-weight: 800;
              font-size: 0.9rem;
              cursor: pointer;
            "
          >
            👨‍🏫 Florin (Parent)
          </button>
        </div>

        <form id="entrance-gate-form" style="display: flex; flex-direction: column; gap: 14px;">
          <input
            id="gate-passcode-input"
            type="password"
            placeholder="Family Passcode (e.g. clawey11)"
            autocomplete="current-password"
            required
            class="touch-target"
            style="
              width: 100%;
              padding: 14px 18px;
              font-size: 1.05rem;
              border: 2px solid ${tokens.color.gildedBrass};
              border-radius: 14px;
              background: #FFFDF9;
              color: ${tokens.color.morrisIndigo};
              box-sizing: border-box;
              text-align: center;
              letter-spacing: 0.05em;
            "
          />

          <div style="display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 0.85rem; color: #4A5B70;">
            <input type="checkbox" id="gate-remember-me" checked style="cursor: pointer;" />
            <label for="gate-remember-me" style="cursor: pointer;">Remember this device (90 days)</label>
          </div>

          <div id="gate-error-message" style="display: none; color: ${tokens.color.madderRed}; font-size: 0.85rem; font-weight: 700;"></div>

          <button
            type="submit"
            id="gate-submit-btn"
            class="touch-target"
            style="
              width: 100%;
              padding: 14px;
              background: ${tokens.color.morrisIndigo};
              color: #FAF8F5;
              border: 2px solid ${tokens.color.gildedBrass};
              border-radius: 14px;
              font-size: 1.05rem;
              font-weight: 800;
              cursor: pointer;
              transition: transform 0.15s ease, background 0.15s ease;
            "
          >
            Enter the Atelier &rarr;
          </button>
        </form>

        <div style="margin-top: 18px; font-size: 0.8rem; color: #7A8B9E;">
          Default Family Passcode: <strong style="color: ${tokens.color.morrisIndigo};">${DEFAULT_FAMILY_PASSCODE}</strong>
        </div>
      </div>
    `;

    // Role switcher events
    const studentBtn = this.overlay.querySelector<HTMLButtonElement>('#role-student-btn');
    const parentBtn = this.overlay.querySelector<HTMLButtonElement>('#role-parent-btn');
    const input = this.overlay.querySelector<HTMLInputElement>('#gate-passcode-input');

    studentBtn?.addEventListener('click', () => {
      this.selectedRole = 'student';
      studentBtn.style.border = `2px solid ${tokens.color.morrisIndigo}`;
      studentBtn.style.background = 'rgba(26,54,93,0.08)';
      if (parentBtn) {
        parentBtn.style.border = '2px solid rgba(26,54,93,0.2)';
        parentBtn.style.background = '#FFFDF9';
      }
      if (input) input.placeholder = `Family Passcode (e.g. ${DEFAULT_FAMILY_PASSCODE})`;
    });

    parentBtn?.addEventListener('click', () => {
      this.selectedRole = 'parent';
      parentBtn.style.border = `2px solid ${tokens.color.morrisIndigo}`;
      parentBtn.style.background = 'rgba(26,54,93,0.08)';
      if (studentBtn) {
        studentBtn.style.border = '2px solid rgba(26,54,93,0.2)';
        studentBtn.style.background = '#FFFDF9';
      }
      if (input) input.placeholder = 'Parent Master Key or Family Passcode';
    });

    // Form submit event
    const form = this.overlay.querySelector<HTMLFormElement>('#entrance-gate-form');
    const rememberBox = this.overlay.querySelector<HTMLInputElement>('#gate-remember-me');
    const errorDiv = this.overlay.querySelector<HTMLElement>('#gate-error-message');
    const submitBtn = this.overlay.querySelector<HTMLButtonElement>('#gate-submit-btn');

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const code = input?.value.trim() || '';
      if (!code) return;

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Unlocking Atelier...';
      }
      if (errorDiv) {
        errorDiv.style.display = 'none';
      }

      const success = await AuthManager.unlockWithFamilyPasscode(
        code,
        this.selectedRole,
        rememberBox?.checked ?? true
      );

      if (success) {
        soundEngine.playSuccess();
        this.close();
        this.onUnlock();
      } else {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Enter the Atelier →';
        }
        if (errorDiv) {
          errorDiv.textContent = `Passcode not recognized. Hint: default family passcode is "${DEFAULT_FAMILY_PASSCODE}"`;
          errorDiv.style.display = 'block';
        }
      }
    });
  }
}
